import { useState } from "react";
import { Link } from "react-router-dom";

import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { dateText } from "@/admin/api";
import { NativeSelect, Pill, TenantPicker } from "@/admin/components";
import type { FieldSpec, Row } from "@/admin/resources/api";

const money = (dollars: number) =>
  `${dollars < 0 ? "-" : ""}$${Math.abs(dollars).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** One value, read-only, as the list and the detail screen show it. */
export function FieldValue({ field, row, full }: { field: FieldSpec; row: Row; full?: boolean }) {
  const v = row[field.name];
  const label = row[`${field.name}__label`] as string | null | undefined;
  switch (field.type) {
    case "bool":
      return v ? <Pill tone="green">Yes</Pill> : <Pill tone="gray">No</Pill>;
    case "choice": {
      const found = field.choices?.find(([c]) => c === v);
      return <span>{found ? found[1] : v ? String(v) : "—"}</span>;
    }
    case "datetime":
      return <span className="whitespace-nowrap">{dateText((v as string) ?? null)}</span>;
    case "cents":
      return <span className="tabular-nums">{v == null ? "—" : money(Number(v) / 100)}</span>;
    case "millicents":
      return <span className="tabular-nums">{v == null ? "—" : money(Number(v) / 100000)}</span>;
    case "token_price":
      return <span className="tabular-nums">{v == null ? "—" : `$${Number(v) / 100000}`}</span>;
    case "tenant":
      if (!v) return <span className="text-muted-foreground">All tenants</span>;
      return (
        <Link to={`/admin/tenants/${v}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
          {label ?? String(v)}
        </Link>
      );
    case "ref":
      return <span>{v ? label ?? String(v) : "—"}</span>;
    case "secret":
      return <span>{row[`${field.name}__set`] ? "Set" : "Not set"}</span>;
    case "json":
      if (!full) return <span className="text-muted-foreground">{v == null ? "—" : "{…}"}</span>;
      return (
        <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(v, null, 2)}</pre>
      );
    case "longtext": {
      const text = (v as string) ?? "";
      if (!text) return <span className="text-muted-foreground">—</span>;
      if (!full) return <span className="line-clamp-1 max-w-xs">{text}</span>;
      return <p className="whitespace-pre-wrap text-sm">{text}</p>;
    }
    default: {
      const text = v == null || v === "" ? "—" : String(v);
      return full ? <span className="break-all">{text}</span> : <span className="line-clamp-1 max-w-xs break-all">{text}</span>;
    }
  }
}

/** One editable value. `value` is what will be sent; `row` gives labels. */
export function FieldInput({
  field,
  value,
  row,
  onChange,
}: {
  field: FieldSpec;
  value: unknown;
  row: Row | null;
  onChange: (v: unknown) => void;
}) {
  switch (field.type) {
    case "bool":
      return <Switch checked={!!value} onCheckedChange={onChange} />;
    case "choice":
      return (
        <NativeSelect value={(value as string) ?? ""} onChange={onChange}>
          <option value="" disabled>
            Choose…
          </option>
          {field.choices?.map(([c, l]) => (
            <option key={c} value={c}>
              {l}
            </option>
          ))}
        </NativeSelect>
      );
    case "token_price":
      // Typed in dollars, stored in millicents.
      return (
        <Input
          type="number"
          min={0}
          step="0.001"
          placeholder="e.g. 0.20"
          value={value == null ? "" : String(Number(value) / 100000)}
          onChange={(e) => onChange(e.target.value === "" ? null : Math.round(Number(e.target.value) * 100000))}
        />
      );
    case "int":
    case "cents":
    case "millicents":
      return (
        <Input
          type="number"
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        />
      );
    case "longtext":
      return <Textarea rows={8} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />;
    case "tenant":
      return (
        <TenantInput
          value={(value as string) || null}
          initialName={row && row[field.name] ? ((row[`${field.name}__label`] as string) ?? "") : ""}
          onChange={onChange}
        />
      );
    case "json":
      return <JsonInput value={value} onChange={onChange} />;
    default:
      return <Input value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

function TenantInput({
  value,
  initialName,
  onChange,
}: {
  value: string | null;
  initialName: string;
  onChange: (v: unknown) => void;
}) {
  const [name, setName] = useState(initialName);
  return (
    <TenantPicker
      value={value ? { id: value, name: name || value } : null}
      onChange={(t) => {
        setName(t?.name ?? "");
        onChange(t?.id ?? null);
      }}
      allowNone
    />
  );
}

function JsonInput({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  return (
    <Textarea
      rows={6}
      className="font-mono text-xs"
      defaultValue={JSON.stringify(value ?? null, null, 2)}
      onBlur={(e) => {
        try {
          onChange(JSON.parse(e.target.value));
        } catch {
          /* left as typed until it parses */
        }
      }}
    />
  );
}
